/**
 * Contagem regressiva (spec 3 e 9.5).
 *
 * Duas regras puras: qual e a PROXIMA ocorrencia da data alvo e quantos dias
 * faltam para ela. Elas existem porque `repeat` transforma uma data fixa em
 * serie: um aniversario com `repeat = 'yearly'` que passou ontem nao virou
 * "-1 dia" — virou "faltam 364".
 *
 * Nada aqui reaproveita `recurrence.ts`, e a diferenca nao e de estilo: aquele
 * modulo opera sobre `due_at` (epoch ms, com hora de parede a preservar) e
 * materializa a proxima instancia AO CONCLUIR. Aqui o dominio e data pura, sem
 * hora, e a ocorrencia e derivada a cada leitura — nao ha nada para gravar.
 */

import type { CountdownRepeat } from './constants.js';
import {
  addDaysToIsoDate,
  daysInMonth,
  isoDateDifference,
  isoDateFromParts,
  isoDateParts,
} from './datetime.js';
import type { IsoDate } from './types.js';

export interface CountdownSchedule {
  targetDate: IsoDate;
  repeat: CountdownRepeat;
}

/**
 * Soma meses a uma data pura, grampeando no ultimo dia valido do mes destino.
 *
 * O grampeamento parte SEMPRE do dia original, nunca do resultado anterior.
 * Encadear (31/jan -> 28/fev -> 28/mar) faria a serie perder o dia 31 para
 * sempre depois de passar uma unica vez por fevereiro.
 */
function addMonthsToIsoDate(date: IsoDate, months: number): IsoDate {
  const { year, month, day } = isoDateParts(date);

  const indice = month - 1 + months;
  const anoDestino = year + Math.floor(indice / 12);
  const mesDestino = (((indice % 12) + 12) % 12) + 1;

  return isoDateFromParts(
    anoDestino,
    mesDestino,
    Math.min(day, daysInMonth(anoDestino, mesDestino)),
  );
}

/** A n-esima ocorrencia da serie, contando a data alvo como a de indice 0. */
function occurrence(schedule: CountdownSchedule, n: number): IsoDate {
  switch (schedule.repeat) {
    case 'weekly':
      return addDaysToIsoDate(schedule.targetDate, 7 * n);
    case 'monthly':
      return addMonthsToIsoDate(schedule.targetDate, n);
    case 'yearly':
      return addMonthsToIsoDate(schedule.targetDate, 12 * n);
    default:
      return schedule.targetDate;
  }
}

/**
 * Proxima ocorrencia com data maior ou igual a hoje.
 *
 * Sem repeticao, devolve a propria data alvo mesmo quando ja passou: uma data
 * unica que passou PASSOU, e empurra-la para o futuro apagaria o unico fato que
 * aquela contagem tinha a dizer.
 *
 * O laco parte de uma estimativa por divisao, e nao do zero. Uma serie semanal
 * de tres anos atras exigiria 150 voltas; a estimativa deixa o laco com duas ou
 * tres, e ele existe apenas para corrigir o arredondamento (mes de 28 a 31
 * dias, ano bissexto).
 */
export function nextCountdownDate(schedule: CountdownSchedule, today: IsoDate): IsoDate {
  if (schedule.repeat === 'none') return schedule.targetDate;

  const atraso = isoDateDifference(schedule.targetDate, today);
  if (atraso <= 0) return schedule.targetDate;

  const passoEmDias =
    schedule.repeat === 'weekly' ? 7 : schedule.repeat === 'yearly' ? 366 : 31;

  // `floor` de proposito: a estimativa precisa ficar AQUEM da resposta para o
  // laco so avancar. Se passasse do alvo, a primeira ocorrencia valida seria
  // pulada em silencio.
  let n = Math.max(0, Math.floor(atraso / passoEmDias));
  let candidata = occurrence(schedule, n);

  while (isoDateDifference(candidata, today) > 0) {
    n += 1;
    candidata = occurrence(schedule, n);
  }

  return candidata;
}

/**
 * Dias ate a proxima ocorrencia. Negativo quando a data ja passou — o que so
 * acontece com `repeat = 'none'`.
 */
export function countdownDaysRemaining(
  schedule: CountdownSchedule,
  today: IsoDate,
): number {
  return isoDateDifference(today, nextCountdownDate(schedule, today));
}
